import { i18n } from '@/i18n/i18n';
import { watch, App, Directive } from 'vue';

// Function to apply interface options to a single ion-select element
function applyInterfaceOptionsToSelect(selectElement: any) {
  const currentLocale = i18n.locale.value;
  const t = i18n.t;
  
  const interfaceOptions = {
    cancelText: t('ionic.cancel', currentLocale),
    okText: t('ionic.OK', currentLocale)
  };
  
  // Set the interface options directly on the element
  selectElement.interfaceOptions = interfaceOptions;
  
  // Also try to set the individual properties as fallback
  selectElement.cancelText = interfaceOptions.cancelText;
  selectElement.okText = interfaceOptions.okText;
}

// Function to update Ionic interface options with current translations
function updateIonicInterfaceOptions() {
  const currentLocale = i18n.locale.value;
  const t = i18n.t;
  
  // Try multiple ways to access Ionic config
  const ionicConfig = (window as any).Ionic?.config || 
                     (window as any).Ionic?.Config ||
                     (window as any).Ionic?.config;
  
  if (ionicConfig && typeof ionicConfig.set === 'function') {
    ionicConfig.set('interfaceOptions', {
      select: {
        cancelText: t('ionic.cancel', currentLocale),
        okText: t('ionic.OK', currentLocale)
      }
    });
  } else {
    // Fallback: try to update the global interface options directly
    const globalIonic = (window as any).Ionic;
    if (globalIonic && globalIonic.config) {
      globalIonic.config.interfaceOptions = {
        select: {
          cancelText: t('ionic.cancel', currentLocale),
          okText: t('ionic.OK', currentLocale)
        }
      };
    }
  }
}

// Watch for locale changes and update interface options
watch(() => i18n.locale.value, () => {
  // Small delay to ensure Ionic is ready
  window.setTimeout(updateIonicInterfaceOptions, 100);
}, { immediate: true });

// Export function for manual updates if needed
export function setupIonicI18n() {
  // Initial setup with a delay to ensure Ionic is fully initialized
  window.setTimeout(updateIonicInterfaceOptions, 200);
}

// Custom directive to apply interface options to ion-select elements
const vIonicSelect: Directive = {
  mounted(el: HTMLElement) {
    if (el.tagName === 'ION-SELECT') {
      // Apply immediately
      applyInterfaceOptionsToSelect(el);
      
      // Also apply after a short delay to ensure Ionic has initialized the element
      window.setTimeout(() => applyInterfaceOptionsToSelect(el), 100);
    }
  },
  updated(el: HTMLElement) {
    if (el.tagName === 'ION-SELECT') {
      applyInterfaceOptionsToSelect(el);
    }
  }
};

// Global plugin to automatically apply translated interface options
export const IonicI18nPlugin = {
  install(app: App) {
    // Register the directive globally
    app.directive('ionic-select', vIonicSelect);
    
    // Create a global mixin to automatically apply interface options to ion-select components
    app.mixin({
      mounted() {
        this.applyIonicSelectTranslations();
        
        // Set up a mutation observer to handle dynamically created ion-select elements
        if (this.$el && this.$el.querySelectorAll) {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                  const element = node as Element;
                  if (element.tagName === 'ION-SELECT') {
                    applyInterfaceOptionsToSelect(element);
                  }
                  // Also check for ion-select elements within the added node
                  const selectElements = element.querySelectorAll('ion-select');
                  selectElements.forEach(applyInterfaceOptionsToSelect);
                }
              });
            });
          });
          
          observer.observe(this.$el, {
            childList: true,
            subtree: true
          });
          
          // Store the observer for cleanup
          this._ionicObserver = observer;
        }
      },
      updated() {
        // Only apply if $el exists and has querySelectorAll method
        if (this.$el && typeof this.$el.querySelectorAll === 'function') {
          this.applyIonicSelectTranslations();
        }
      },
      beforeUnmount() {
        // Clean up the observer
        if (this._ionicObserver) {
          this._ionicObserver.disconnect();
        }
      },
      methods: {
        applyIonicSelectTranslations() {
          // Check if this component has ion-select elements
          if (this.$el && typeof this.$el.querySelectorAll === 'function') {
            const selectElements = this.$el.querySelectorAll('ion-select');
            if (selectElements && selectElements.length > 0) {
              selectElements.forEach(applyInterfaceOptionsToSelect);
            }
          }
        }
      }
    });
    
    // Add a global watcher for locale changes that updates all ion-select elements
    watch(() => i18n.locale.value, () => {
      // Update all ion-select elements in the DOM when locale changes
      window.setTimeout(() => {
        const allSelectElements = document.querySelectorAll('ion-select');
        allSelectElements.forEach(applyInterfaceOptionsToSelect);
      }, 100);
    });
  }
}; 